import './style.css';
import { mountAxis } from './axis/axis-view.ts';
import { mountReach } from './axis/reach.ts';
import { mountToolbar } from './axis/toolbar.ts';
import { startData, type Snapshot } from './data/data.ts';
import { mountKreska } from './kreska/kreska.ts';
import { mountInstall } from './screens/install.ts';
import { mountScreens } from './screens/screens.ts';
import { markReady, mount } from './shell.ts';
import { memory, remembered } from './state/memory.ts';
import { viewState } from './state/viewstate.ts';
import { theme, type ThemePref } from './theme.ts';
import { mountWheel } from './wheel/wheel.ts';

// The address is tidied before anything is read from it or written to it: junk
// keys and a non-canonical order are rewritten away, with no history entry and
// no change to the view (§4.2).
viewState.normalise();

// What this browser remembers is applied before anything renders, and only where
// the address is silent — a pasted link always wins (§4.6). Restoring is not an
// opening, so it writes no history entry.
viewState.set(remembered(viewState.get()), 'replace');

mountWheel();
// Before the kreska: the kreska sizes the circle block from what the column's
// other boxes leave (SPEC-viewfinder §2.2), and the toolbar is one of them. So
// is „Dodaj do ekranu" where the browser can install the app at all (#348) —
// on Android the offer arrives later and the band appears then, which takes
// its 45 px off the rail rather than off a block already written.
mountInstall();
mountToolbar();
mountKreska();
mountScreens();
mountAxis();
// After the rail, so a `sel` from the address widens the ring into a list that
// is already mounted and will be told of the change (§6, #225).
mountReach();

// `ⓘ` is in the bar at every depth (§3.2). Opening a screen is one history
// entry, so Back closes it.
mount('topbar')
  .querySelector('#help')
  ?.addEventListener('click', () => {
    viewState.set({ screen: 'pomoc' }, 'open');
  });

// ◐ systemowy → ○ jasny → ● ciemny (§4.1). The glyph shows the preference, not
// the resolved theme: „systemowy" on a dark OS is still „systemowy".
const GLYPHS: Record<ThemePref, { glyph: string; name: string }> = {
  system: { glyph: '◐', name: 'systemowy' },
  light: { glyph: '○', name: 'jasny' },
  dark: { glyph: '●', name: 'ciemny' },
};
const ORDER: ThemePref[] = ['system', 'light', 'dark'];
const glyph = mount('topbar').querySelector('#theme-glyph');

function paintThemeGlyph() {
  if (!glyph) return;
  const { glyph: mark, name } = GLYPHS[theme.get()];
  glyph.textContent = mark;
  glyph.setAttribute('aria-label', `Motyw: ${name}. Dotknij, żeby zmienić`);
}
glyph?.addEventListener('click', () => {
  const next = ORDER[(ORDER.indexOf(theme.get()) + 1) % ORDER.length];
  if (next) theme.set(next);
  paintThemeGlyph();
});
theme.subscribe(paintThemeGlyph);

// Esc unwinds one opening, wherever the resident is. Tapping the paper closes
// nothing (§4.2).
addEventListener('keydown', (e) => {
  if (e.key === 'Escape') viewState.back();
});

// The intro counts runs, not visits within a run (§5.9); S4 reads it.
memory.runs.bump();

let dataIsIn = false;
startData((snapshot: Snapshot) => {
  // S1–S4 render from this. Until they do, the seam still has to prove it runs:
  // the first snapshot is half of what `data-ready` waits for (§6.5).
  void snapshot;
  if (dataIsIn) return;
  dataIsIn = true;
  markReady('data');
});
